import type { PostHog } from 'posthog-js';

const STORAGE_KEY = 'porterWaitlistArrival';
// Long enough for a same-day click-through (including the full page reload
// after confirmation), short enough that later organic bookings aren't
// attributed to the waitlist.
const ARRIVAL_TTL_MS = 24 * 60 * 60 * 1000;

export type WaitlistArrival = {
  /** Freed slot window from the notification link (epoch ms), when known. */
  slotStart: number | null;
  slotEnd: number | null;
  landingPath: string;
  arrivedAt: number;
};

let posthogPromise: Promise<PostHog | null> | null = null;

// posthog-js is loaded lazily so visitors who never arrive from a waitlist
// notification pay no bundle or network cost.
function getPosthog(): Promise<PostHog | null> {
  if (!posthogPromise) {
    const key = process.env.REACT_APP_POSTHOG_KEY;
    posthogPromise = key
      ? import('posthog-js')
          .then(({ default: posthog }) => {
            // eslint-disable-next-line no-underscore-dangle
            if (!posthog.__loaded) {
              posthog.init(key, {
                api_host: 'https://us.i.posthog.com',
                autocapture: false,
                capture_pageview: false,
                capture_pageleave: false,
              });
            }
            return posthog;
          })
          .catch(() => null)
      : Promise.resolve(null);
  }
  return posthogPromise;
}

export function getWaitlistArrival(): WaitlistArrival | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const arrival = JSON.parse(raw) as WaitlistArrival;
    if (!arrival.arrivedAt || Date.now() - arrival.arrivedAt > ARRIVAL_TTL_MS) {
      return null;
    }
    return arrival;
  } catch {
    return null;
  }
}

/** No-op unless the visitor arrived from a waitlist notification link. */
export function trackWaitlistEvent(
  event: string,
  properties: Record<string, unknown> = {},
) {
  const arrival = getWaitlistArrival();
  if (!arrival) return;
  getPosthog().then(posthog => {
    posthog?.capture(event, {
      slot_start: arrival.slotStart
        ? new Date(arrival.slotStart).toISOString()
        : null,
      slot_end: arrival.slotEnd
        ? new Date(arrival.slotEnd).toISOString()
        : null,
      landing_path: arrival.landingPath,
      ...properties,
    });
  });
}

/** Join-flow decision points; unlike trackWaitlistEvent, not arrival-gated. */
export function trackWaitlistJoinEvent(
  event: string,
  properties: Record<string, unknown> = {},
) {
  getPosthog()
    .then(posthog => {
      posthog?.capture(event, {
        path: window.location.pathname,
        ...properties,
      });
    })
    .catch(() => {
      // analytics must never break the booking flow
    });
}

/**
 * Call once on app load. Persists attribution from ?fromWaitlist notification
 * links to localStorage so it survives the full page reload after
 * confirmation (and the Stripe BNPL round-trip).
 */
export function captureWaitlistArrival() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('fromWaitlist') !== '1') return;
  const arrival: WaitlistArrival = {
    slotStart: Number(params.get('ws')) || null,
    slotEnd: Number(params.get('we')) || null,
    landingPath: window.location.pathname,
    arrivedAt: Date.now(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arrival));
  } catch {
    // Attribution can't survive the confirm reload; skip tracking entirely
    // rather than emit a funnel that always dies at landing.
    return;
  }
  trackWaitlistEvent('waitlist_landed');
}
